import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Container,
  Group,
  Menu,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAdjustments,
  IconAlertTriangle,
  IconArrowLeft,
  IconDots,
  IconInfoCircle,
  IconMapPin,
  IconPencil,
  IconTrash,
  IconWheat,
} from '@tabler/icons-react';
import EmptyState from '@/components/common/EmptyState';
import PriceGrid from '@/components/app/buy/PriceGrid';
import OfferTerms from '@/components/app/buy/OfferTerms';
import CropYearBadge from '@/components/app/buy/CropYearBadge';
import UpdatedDateBadge from '@/components/app/buy/UpdatedDateBadge';
import BulkPriceDialog from '@/components/app/buy/BulkPriceDialog';
import { orderService } from '@/api/services/OrderService';
import { BUY_TREE_QUERY_KEY, useBuyBestPrices, usePurchasePriceList, type PriceListQuery } from '@/hooks/useBuy';
import { buildBestPriceMap } from '@/lib/bestPrice';
import { getApiMessage, isServiceUnavailable } from '@/api/errors';
import { ROUTES } from '@/routes/paths';
import { formatOrgName } from '@/lib/format';

// Минимальная цена строки на платформе (₽/т) — ограничивает массовое уменьшение.
const PRICE_FLOOR = 1000;

// Деталь группы прайс-листа (drill-in). Год урожая в UI не показываем — он лишь в query/API.
export default function BuyGroupPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const address = params.get('address');
  const addressTitle = params.get('addressTitle');
  const goods = params.get('goods');
  // Нет параметра — NaN, а не 0: Number(null) === 0 прошёл бы проверку и показал «Урожай 0».
  const yearParam = params.get('year');
  const year = yearParam === null ? Number.NaN : Number(yearParam);

  // Хуки вызываем всегда (Rules of Hooks); при невалидных параметрах query=null → запросы выключены.
  const query: PriceListQuery | null =
    address && addressTitle && goods && !Number.isNaN(year)
      ? { address, addressTitle, goods, year }
      : null;
  const { data, isLoading, isError, error, refetch } = usePurchasePriceList(query);

  // Лучшие цены продажи под мои строки (мягко: ошибка/пусто → грид просто без рынка).
  const orderIds = useMemo(
    () =>
      data?.goodsList.flatMap((item) =>
        item.qualityRows.map((row) => row.orderId).filter((id): id is number => id != null),
      ) ?? [],
    [data],
  );
  const bestParams = useMemo(
    () =>
      query && orderIds.length > 0
        ? {
            purchaseOrderIds: orderIds,
            address: query.address,
            goodsTypeParent: query.goods,
            cropYear: query.year,
          }
        : null,
    // query пересоздаётся каждый рендер — зависим от его примитивов.
    [orderIds, query?.address, query?.goods, query?.year],
  );
  const bestQuery = useBuyBestPrices(bestParams);
  const bestPrices = useMemo(() => buildBestPriceMap(bestQuery.data), [bestQuery.data]);

  // ── Действия (Фаза 3): массовый Δ цен и удаление группы ────────────────────────
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bulkOpened, setBulkOpened] = useState(false);

  // Максимально допустимое уменьшение: минимальная из моих цен не должна уйти ниже пола.
  const maxDiscount = useMemo(() => {
    const prices =
      data?.goodsList.flatMap((item) =>
        item.qualityRows.flatMap((row) =>
          Object.values(row.pricesByPrepaymentMap ?? {}).filter((p): p is number => p != null),
        ),
      ) ?? [];
    if (prices.length === 0) {
      return 0;
    }
    return Math.max(0, Math.min(...prices) - PRICE_FLOOR);
  }, [data]);

  const onActionError = (error: unknown): void => {
    notifications.show({
      color: 'red',
      message: isServiceUnavailable(error)
        ? t('auth.errors.unavailable')
        : (getApiMessage(error) ?? t('auth.errors.generic')),
    });
  };

  const bulk = useMutation({
    mutationFn: (delta: number) =>
      orderService.updatePurchasePrices({
        address: query!.address,
        addressTitle: query!.addressTitle,
        parentGoodsType: query!.goods,
        cropYear: query!.year,
        delta,
      }),
    onSuccess: () => {
      setBulkOpened(false);
      queryClient.invalidateQueries({ queryKey: ['purchasePriceList'] });
      queryClient.invalidateQueries({ queryKey: ['buyBestPrices'] });
      notifications.show({ color: 'lime', message: t('app.buy.bulk.applied') });
    },
    onError: onActionError,
  });

  const removeGroup = useMutation({
    mutationFn: () =>
      orderService.deletePurchaseGroup({
        address: query!.address,
        addressTitle: query!.addressTitle,
        parentGoodsType: query!.goods,
        year: query!.year,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUY_TREE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['purchasePriceList'] });
      notifications.show({ color: 'lime', message: t('app.buy.del.deleted') });
      navigate(ROUTES.APP_BUY);
    },
    onError: onActionError,
  });

  function confirmDeleteGroup(): void {
    // Число удаляемых позиций (заявок с ценой) — чтобы сотрудник видел масштаб перед удалением общего прайса.
    const positions = data?.goodsList.flatMap((item) => item.qualityRows).filter((row) => row.orderId != null).length ?? 0;
    modals.openConfirmModal({
      title: t('app.buy.del.groupTitle'),
      children: (
        <Text size="sm">
          {t('app.buy.del.groupConfirm', { goods: query!.goods, basis: formatOrgName(query!.addressTitle), count: positions })}
        </Text>
      ),
      labels: { confirm: t('app.buy.del.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeGroup.mutate(),
    });
  }

  // Глубокая ссылка без нужных параметров → назад в список.
  if (query === null) {
    return <Navigate to={ROUTES.APP_BUY} replace />;
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Stack gap="xs">
          {/* Назад в список с сохранением фильтра (базис+культура), который пользователь видел до открытия. */}
          <Anchor
            component={Link}
            to={`${ROUTES.APP_BUY}?${new URLSearchParams({ address: query.address, addressTitle: query.addressTitle, goods: query.goods }).toString()}`}
            size="sm"
            c="brand.7"
          >
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {t('app.buy.back')}
            </Group>
          </Anchor>
          <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
            {/* flex:1 + minWidth:0 — длинный адрес усечётся, а действия останутся в строке шапки. */}
            <Group gap="sm" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
              <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0 }}>
                <IconWheat size={26} />
              </ThemeIcon>
              <Stack gap={4} style={{ minWidth: 0 }}>
                <Title order={2}>{query.goods}</Title>
                <Group gap={6} wrap="nowrap">
                  <IconMapPin size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
                  <Text fw={500} style={{ flexShrink: 0 }}>
                    {formatOrgName(query.addressTitle)}
                  </Text>
                  <Text size="sm" c="gray.7" truncate title={query.address}>
                    · {query.address}
                  </Text>
                </Group>
                <Group gap={8} wrap="wrap" align="center">
                  <Group gap={6} wrap="nowrap">
                    <IconInfoCircle size={16} color="var(--mantine-color-gray-7)" />
                    <Text size="sm" c="gray.8">
                      {t('app.buy.unitsNote')}
                    </Text>
                  </Group>
                  <CropYearBadge year={query.year} />
                  <UpdatedDateBadge date={data?.updatedDate} />
                </Group>
              </Stack>
            </Group>
            {data && data.goodsList.length > 0 ? (
              // Ранги действий: рутина (± Цены) — нейтральная; главное (Редактировать) — залитая;
              // редкое деструктивное (Удалить) — в меню «⋯», как в списке и матрице.
              <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                <Tooltip label={t('app.buy.bulk.hint')}>
                  <Button
                    variant="default"
                    leftSection={<IconAdjustments size={16} />}
                    onClick={() => setBulkOpened(true)}
                  >
                    {t('app.buy.bulk.button')}
                  </Button>
                </Tooltip>
                <Button
                  component={Link}
                  to={`${ROUTES.APP_BUY_EDIT}?${params.toString()}`}
                  leftSection={<IconPencil size={16} />}
                >
                  {t('app.buy.form.editButton')}
                </Button>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="default"
                      size={36}
                      aria-label={t('app.buy.del.groupTitle')}
                      loading={removeGroup.isPending}
                    >
                      <IconDots size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={confirmDeleteGroup}>
                      {t('app.buy.del.groupTitle')}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            ) : null}
          </Group>
        </Stack>

        {isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
            <Group justify="space-between" wrap="nowrap">
              <Text>{isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}</Text>
              <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        ) : isLoading || !data ? (
          <Stack gap="sm">
            <Skeleton h={28} w={220} />
            <Skeleton h={180} />
          </Stack>
        ) : data.goodsList.length === 0 ? (
          <EmptyState decorated={false} icon={<IconAlertTriangle size={30} />} title={t('app.buy.groupEmpty')} />
        ) : (
          <Stack gap="lg" className="app-fade-in">
            <PriceGrid
              list={data}
              bestPrices={bestPrices}
              bestPricesLoading={bestQuery.isLoading}
              bestPricesError={bestQuery.isError}
            />
            <OfferTerms list={data} />
          </Stack>
        )}

        <BulkPriceDialog
          opened={bulkOpened}
          goods={query.goods}
          basisTitle={formatOrgName(query.addressTitle)}
          maxDiscount={maxDiscount}
          loading={bulk.isPending}
          onApply={(delta) => bulk.mutate(delta)}
          onClose={() => setBulkOpened(false)}
        />
      </Stack>
    </Container>
  );
}
